import { useEffect } from 'react';
import { renderRoot } from 'quartz-core/client';
import UIApplication from 'UIComponents/app/UIApplication';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import Raven from 'raven-js';
import { ThemeProvider } from 'academy-common-lib/utils/Styling';
import dataFetchingClient from './utils/dataFetchingClient';
import { DataFetchingClientProvider } from 'academy-common-lib/utils/DataFetchingClient';

// I18n Configuration
import createPublicI18nProvider from 'I18n/init/providers/createPublicI18nProvider';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import registerAllData from 'I18n/init/register/sync/registerAllData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';

// Import extra I18n data
import 'I18n/init/data/currency';
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/publicLocales';

// Rhumb
import { RhumbProvider } from 'react-rhumb';
import rhumbConfig from '../rhumb-config.yaml';
import historyAdapter from 'react-rhumb/historyAdapterV4';

// Configure the router
import { Router } from 'quartz-routing/react-router';
import { Routes } from './routes/Routes';
import { getPublicLang } from 'academy-common-lib/utils/url';
import ReactRouterScrollToTop from 'academy-common-lib/components/routing/ReactRouterScrollToTop';
import { getHistory } from './routing/history';
import { getDeviceId, getUtk } from 'usage-tracker-core/common/defaultTrackers';
import { ConditionalThemeProvider } from 'academy-common-lib/features/publicTheme/ConditionalThemeProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Compliance = () => {
  useEffect(() => {
    try {
      // @ts-expect-error _hsg is provided by compliance script in index.html.tsx
      if (window._hsg) {
        // @ts-expect-error _hsg is provided by compliance script in index.html.tsx
        window._hsg.compliance.scanCookies();
        const timeout = setTimeout(() => {
          const hiddenFooterCookieSettings = document.querySelector('.hs-footer-cookie-settings[hidden]');
          if (hiddenFooterCookieSettings) {
            // @ts-expect-error _hsg is provided by compliance script in index.html.tsx
            window._hsg.compliance.addSettingsLink();
          }
        }, 200);
        return () => {
          clearTimeout(timeout);
        };
      }
    } catch (error) {
      Raven.captureException(error);
      console.error(error);
    }
  }, []);
  return null;
};
export class App {
  // Kicks off the app. This is intentionally not in the constructor.
  async start() {
    const locale = getPublicLang();
    const I18nProvider = createPublicI18nProvider({
      manuallySetLocale: true
    });
    I18nProvider.setLocale({
      locale,
      langEnabled: true
      // timezone: portal && portal.timezone,
    });
    Raven.setUserContext({
      // @ts-expect-error we don't have access to all data required by Raven types
      locale
    });
    await initAppTranslations([I18nProvider.register(lang), registerAllData(I18nProvider)]);
    setDocumentTitle('academyPublic.appTitle');
    this.startRouter();
    const utk = getUtk();
    const anonymousId = getDeviceId();
    const labsScript = document.createElement('script');
    labsScript.src = `https://js.hubspot.com/lab-js/v1/ws/standalone.js?utk=${utk}&other=${anonymousId}&referer=${encodeURIComponent(window.location.origin)}`; // referer is always just the origin as it's a SPA, and exposure page selection is handled by the treatment snippet
    labsScript.async = true;
    document.head.appendChild(labsScript);
  }
  startRouter() {
    const history = getHistory();
    renderRoot( /*#__PURE__*/_jsxs(UIApplication, {
      name: "academy-public-ui",
      children: [/*#__PURE__*/_jsx(Compliance, {}), /*#__PURE__*/_jsx(ConditionalThemeProvider, {
        children: /*#__PURE__*/_jsx(ThemeProvider, {
          children: /*#__PURE__*/_jsxs(DataFetchingClientProvider, {
            client: dataFetchingClient,
            children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(RhumbProvider, {
              config: rhumbConfig,
              history: historyAdapter(history),
              children: /*#__PURE__*/_jsxs(Router, {
                history: history,
                children: [/*#__PURE__*/_jsx(ReactRouterScrollToTop, {}), /*#__PURE__*/_jsx(Routes, {})]
              })
            })]
          })
        })
      })]
    }));
  }
}